import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IWeeklySchedulerOptions, IWeeklySchedulerRange } from '@captiveaire/weeklyscheduler';
import { ITimeRange } from '@caslink/common-client';
import { StringUtilsService } from '@caslink/common-client/ng-app/utilities/StringUtils';
import * as _ from 'lodash';
import { RtuSetpointEditorPopupComponent } from '../rtu-setpoint-editor-popup/rtu-setpoint-editor-popup.component';
import { IScheduleSetpointModel, ISetpointModel } from '../shared/models/models';

interface RtuScheduleSlotEditorData {
    areaId: number;
    hardwareItemId: number;
    locationId: number;
    nullEnds: boolean;
    schedule: IWeeklySchedulerRange<string>;
    setpoints: IScheduleSetpointModel[];
    weeklySchedulerOptions: IWeeklySchedulerOptions<string>;
}

// tslint:disable max-line-length
@Component({
    selector: 'dl-rtu-schedule-slot-editor',
    template: `
        <popup-header text="{{ actionText }} Slot" (clickAction)="close()"></popup-header>

        <div mat-dialog-content [formGroup]="formGroup">
            <mat-radio-group [formControl]="radioFormControl">
                <mat-radio-button *ngFor="let setpoint of data.setpoints" [value]="setpoint.name">
                    <dl-setpoint [setpointModel]="setpoint"></dl-setpoint>
                </mat-radio-button>
            </mat-radio-group>

            <cl-time-range [formGroup]="formGroup" [interval]="data.weeklySchedulerOptions.interval" [nullEnds]="data.nullEnds" [(timeRange)]="timeRange"></cl-time-range>
        </div>

        <popup-button-container>
            <cas-button materialIcon="cancel" (clickAction)="close()" btnText="Cancel"></cas-button>
            <cas-button materialIcon="add" btnClass="md-raised md-accent" (clickAction)="newSetpoint()" btnText="New Setpoint"></cas-button>
            <cas-button btnClass="md-raised md-accent" (clickAction)="save()" [btnText]="actionText" [materialIcon]="actionIcon" [disabled]="formGroup.invalid"></cas-button>
        </popup-button-container>
    `
})
export class RtuSchedulerSlotEditorComponent implements OnInit {
    public static $name = 'rtuScheduleSlotEditor';

    actionIcon: string;
    actionText: string;
    timeRange: ITimeRange;
    formGroup: FormGroup;
    radioFormControl: FormControl;

    private startTime: string;
    private endTime: string;

    constructor(
        private dialogService: MatDialog,
        private dialogRef: MatDialogRef<RtuSchedulerSlotEditorComponent>,
        @Inject(MAT_DIALOG_DATA) public data: RtuScheduleSlotEditorData,
        private stringUtils: StringUtilsService
    ) {
    }

    public ngOnInit() {
        this.startTime = this.stringUtils.secondsToTimeString(this.data.schedule.start);
        this.endTime = this.stringUtils.secondsToTimeString(this.data.schedule.end);

        let rangeClass = this.data.schedule.$class;

        if (!rangeClass && this.data.setpoints.length >= 1) {
            rangeClass = this.data.setpoints[0].name;
        }

        this.timeRange = {
            start: this.data.schedule.start,
            end: this.data.schedule.end,
            startTime: this.startTime,
            endTime: this.endTime,
            canEdit: true,
            rangeClass: rangeClass
        };

        this.actionIcon = this.data.schedule.$isEditing ? 'edit' : 'add';
        this.actionText = this.data.schedule.$isEditing ? 'Edit' : 'Add';

        this.formGroup = new FormGroup({});
        this.radioFormControl = new FormControl(this.timeRange.value);
        this.formGroup.addControl('radio', this.radioFormControl);
    }

    public close() {
        this.dialogRef.close();
    }

    public save() {
        this.dialogRef.close(this.timeRange);
    }

    public newSetpoint() {
        const dialogRef = this.dialogService.open(RtuSetpointEditorPopupComponent, {
            data: {
                areaId: this.data.areaId,
                locationId: this.data.locationId,
                hardwareItemId: this.data.hardwareItemId,
                setpoint: {}
            }
        });

        dialogRef.afterClosed().subscribe((newSetpoint: ISetpointModel) => {
            if (newSetpoint) {
                // in the unlikely event that someone created an identical setpoint
                // to one that already exists, we don't want to show both of them
                if (_.findIndex(this.data.setpoints, (s) => s.name === newSetpoint.name) === -1) {
                    this.data.setpoints.push(newSetpoint);
                }

                this.timeRange.rangeClass = newSetpoint.name;
            }
        });
    }
}
